<script setup>
// Drill-down tables for BI widgets that displays the underlying row-level records (tasks, forms, documents, transmittals, defects, or activities) when a user clicks into a widget.
// Fetches and maps the relevant resource data based on the widget type and applied filters, then renders it in a paginated table view.
// https://docs.superhuman.com/d/_dSQGSQMmTC6/Drill-down-tables_sujWPIX-
import { cloneDeep, has, isNil, uniqBy } from 'lodash-es';
import { onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import BiTableWidget from '~/bi/components/widgets/table-widget/bi-table-widget.vue';
import { useCustomRenders } from '~/bi/components/widgets/table-widget/composables/custom-renders.js';
import { useBiStore } from '~/bi/store/bi.store';
import HawkLoader from '~/common/components/atoms/hawk-loader.vue';

const props = defineProps({
  filters: {
    type: Object,
    default: () => {},
  },
  type: {
    type: String,
    default: 'table',
  },
  widget: {
    type: Object,
  },
  binChange: {
    type: Object,
  },
});

const emit = defineEmits(['close']);

const bi_store = useBiStore();
const route = useRoute();
const router = useRouter();
const { breadcrumbRenderer, tagsRenderer, priorityRenderer, statusRenderer, progressRenderer } = useCustomRenders();

const widget = cloneDeep(props.widget);

const state = reactive({
  widget_data: [],
  widget_metadata: {},
  resource_id_column: null,
  resource_id_values: [],
  is_loading: false,
  tasks: [],
  is_tasks_view: false,
  forms: [],
  is_forms_view: false,
  activities: [],
  is_activities_view: false,
  documents: [],
  is_documents_view: false,
  transmittals: [],
  is_transmittals_view: false,
  defects: [],
  is_defects_view: false,
});

// NOTE: The mappings here denote the column names that need to be fetched and mapped. The mappings inside the computed properties denote the final column names that will be displayed.
const tasks_mapping = {
  'Tasks ⇒ ID': 'ID',
  'Tasks ⇒ Name': 'Name',
  'Tasks ⇒ Category': 'Category',
  'Tasks ⇒ Priority': 'Priority',
  'Tasks ⇒ Assignees': 'Assignees',
  'Tasks ⇒ Due date': 'Due date',
  'Tasks ⇒ Status': 'Status',
  'Tasks ⇒ Tags': 'Tags',
  'Tasks ⇒ Asset': 'Asset',
  'Tasks ⇒ Attached to': 'Attached to',
};

const forms_mapping = {
  'All forms ⇒ ID': 'ID',
  'All forms ⇒ Name': 'Name',
  'All forms ⇒ Category': 'Category',
  'All forms ⇒ Priority': 'Priority',
  'All forms ⇒ Assignees': 'Assignees',
  'All forms ⇒ Due date': 'Due date',
  'All forms ⇒ Status': 'Status',
  'All forms ⇒ Asset': 'Asset',
  'All forms ⇒ Attached to': 'Attached to',
  'All forms ⇒ Tags': 'Tags',
  'Forms ⇒ ID': 'ID',
  'Forms ⇒ Name': 'Name',
  'Forms ⇒ Category': 'Category',
  'Forms ⇒ Priority': 'Priority',
  'Forms ⇒ Assignees': 'Assignees',
  'Forms ⇒ Due date': 'Due date',
  'Forms ⇒ Status': 'Status',
  'Forms ⇒ Asset': 'Asset',
  'Forms ⇒ Attached to': 'Attached to',
  'Forms ⇒ Tags': 'Tags',
};

const documents_mapping = {
  'Documents ⇒ ID': 'ID',
  'Documents ⇒ Name': 'Name',
  'Documents ⇒ Number': 'Number',
  'Documents ⇒ Category': 'Category',
  'Documents ⇒ Status': 'Status',
  'Documents ⇒ Tags': 'Tags',
  'Documents ⇒ Asset': 'Asset',
  'Documents ⇒ Path': 'Path',
};

const transmittals_mapping = {
  'Transmittals ⇒ ID': 'ID',
  'Transmittals ⇒ Name': 'Name',
  'Transmittals ⇒ Issue purpose': 'Issue purpose',
  'Transmittals ⇒ Category': 'Category',
  'Transmittals ⇒ Created at': 'Created at',
  'Transmittals ⇒ Created by': 'Created by',
  'Transmittals ⇒ Cancelled': 'Cancelled',
  'Transmittals ⇒ Completed': 'Completed',
  'Transmittals ⇒ Published': 'Published',
};

const activities_mapping = {
  'Activities ⇒ UID': 'UID',
  'Activities ⇒ ID': 'ID',
  'Activities ⇒ Name': 'Name',
  'Activities ⇒ Actual progress': 'Progress',
  'Activities ⇒ Status': 'Status',
  'Activities ⇒ Planned start': 'Planned start',
  'Activities ⇒ Planned finish': 'Planned finish',
  'Activities ⇒ Actual start': 'Actual start',
  'Activities ⇒ Actual finish': 'Actual finish',
};

const defects_mapping = {
  'Defects ⇒ ID': 'ID',
  'Defects ⇒ Name': 'Name',
  'Defects ⇒ Defect Type': 'Defect Type',
  'Defects ⇒ Status': 'Status',
  'Defects ⇒ Priority': 'Priority',
  'Defects ⇒ Assignees': 'Assignees',
};

const tasks_custom_data = computed(() => {
  const metadata = cloneDeep(state.widget_metadata);
  const columns_map = {
    'Tasks ⇒ Name': 'Name',
    'Tasks ⇒ Category': 'Category',
    'Tasks ⇒ Priority': 'Priority',
    'Tasks ⇒ Assignees': 'Assignees',
    'Tasks ⇒ Due date': 'Due date',
    'Tasks ⇒ Status': 'Status',
    'Tasks ⇒ Tags': 'Tags',
  };
  metadata.columns = Object.keys(columns_map).reduce((acc, col) => {
    const col_from_meta_data = metadata.columns.find(c => c.name === col);
    if (col_from_meta_data) {
      acc.push({
        ...col_from_meta_data,
        name: columns_map[col],
      });
    }
    return acc;
  }, []);

  const data = state.widget_data.map((row) => {
    return normalizeRow(row, 'tasks');
  });

  return {
    metadata,
    data,
  };
});

const forms_custom_data = computed(() => {
  const metadata = cloneDeep(state.widget_metadata);
  const columns_map = {
    'All forms ⇒ Name': 'Name',
    'All forms ⇒ Category': 'Category',
    'All forms ⇒ Priority': 'Priority',
    'All forms ⇒ Assignees': 'Assignees',
    'All forms ⇒ Due date': 'Due date',
    'All forms ⇒ Status': 'Status',
    'All forms ⇒ Tags': 'Tags',
    'Forms ⇒ Name': 'Name',
    'Forms ⇒ Category': 'Category',
    'Forms ⇒ Priority': 'Priority',
    'Forms ⇒ Assignees': 'Assignees',
    'Forms ⇒ Due date': 'Due date',
    'Forms ⇒ Status': 'Status',
    'Forms ⇒ Tags': 'Tags',
  };
  metadata.columns = Object.keys(columns_map).reduce((acc, col) => {
    const col_from_meta_data = metadata.columns.find(c => c.name === col);
    if (col_from_meta_data) {
      acc.push({
        ...col_from_meta_data,
        name: columns_map[col],
      });
    }
    return acc;
  }, []);
  const data = state.widget_data.map((row) => {
    return normalizeRow(row, 'forms');
  });

  return {
    metadata,
    data,
  };
});

const documents_custom_data = computed(() => {
  const metadata = cloneDeep(state.widget_metadata);
  const columns_map = {
    'Documents ⇒ Name': 'Name',
    'Documents ⇒ Number': 'Number',
    'Documents ⇒ Category': 'Category',
    'Documents ⇒ Status': 'Status',
    'Documents ⇒ Tags': 'Tags',
  };
  metadata.columns = Object.keys(columns_map).reduce((acc, col) => {
    const col_from_meta_data = metadata.columns.find(c => c.name === col);
    if (col_from_meta_data) {
      acc.push({
        ...col_from_meta_data,
        name: columns_map[col],
      });
    }
    return acc;
  }, []);
  const data = state.widget_data.map((row) => {
    return normalizeRow(row, 'documents');
  });

  return {
    metadata,
    data,
  };
});

const transmittals_custom_data = computed(() => {
  const metadata = cloneDeep(state.widget_metadata);
  const columns_map = {
    'Transmittals ⇒ Name': 'Name',
    'Transmittals ⇒ Issue purpose': 'Issue purpose',
    'Transmittals ⇒ Category': 'Category',
    'Transmittals ⇒ Created at': 'Created at',
    'Transmittals ⇒ Created by': 'Created by',
  };
  metadata.columns = Object.keys(columns_map).reduce((acc, col) => {
    const col_from_meta_data = metadata.columns.find(c => c.name === col);
    if (col_from_meta_data) {
      acc.push({
        ...col_from_meta_data,
        name: columns_map[col],
      });
    }
    return acc;
  }, []);
  metadata.columns.splice(1, 0, {
    name: 'Status',
    type: 'text',
    pgType: 'text',
  });
  const data = state.widget_data.map((row) => {
    return normalizeRow(row, 'transmittals');
  });

  return {
    metadata,
    data,
  };
});

const activities_custom_data = computed(() => {
  const metadata = cloneDeep(state.widget_metadata);
  const columns_map = {
    'Activities ⇒ ID': 'ID',
    'Activities ⇒ Name': 'Name',
    'Activities ⇒ Actual progress': 'Progress',
    'Activities ⇒ Status': 'Status',
    'Activities ⇒ Planned start': 'Planned start',
    'Activities ⇒ Planned finish': 'Planned finish',
    'Activities ⇒ Actual start': 'Actual start',
    'Activities ⇒ Actual finish': 'Actual finish',
  };
  metadata.columns = Object.keys(columns_map).reduce((acc, col) => {
    const col_from_meta_data = metadata.columns.find(c => c.name === col);
    if (col_from_meta_data) {
      acc.push({
        ...col_from_meta_data,
        name: columns_map[col],
      });
    }
    return acc;
  }, []);
  const data = state.widget_data.map((row) => {
    return normalizeRow(row, 'activities');
  });

  return {
    metadata,
    data,
  };
});

const defects_custom_data = computed(() => {
  const metadata = cloneDeep(state.widget_metadata);
  const columns_map = {
    'Defects ⇒ Name': 'Name',
    'Defects ⇒ Defect Type': 'Defect Type',
    'Defects ⇒ Status': 'Status',
    'Defects ⇒ Priority': 'Priority',
    'Defects ⇒ Assignees': 'Assignees',
  };
  metadata.columns = Object.keys(columns_map).reduce((acc, col) => {
    const col_from_meta_data = metadata.columns.find(c => c.name === col);
    if (col_from_meta_data) {
      acc.push({
        ...col_from_meta_data,
        name: columns_map[col],
      });
    }
    return acc;
  }, []);
  const data = state.widget_data.map((row) => {
    return normalizeRow(row, 'defects');
  });

  return {
    metadata,
    data,
  };
});

function normalizeRow(row, module) {
  const mapping = {
    tasks: tasks_mapping,
    forms: forms_mapping,
    documents: documents_mapping,
    activities: activities_mapping,
    transmittals: transmittals_mapping,
    defects: defects_mapping,
  };

  const newRow = { ...row };
  Object.entries(mapping[module]).forEach(([sourceKey, targetKey]) => {
    if (Object.prototype.hasOwnProperty.call(row, sourceKey)) {
      newRow[targetKey] = row[sourceKey];
    }
  });

  if (module === 'tasks') {
    newRow.Name_breadcrumb = {
      value: row['Tasks ⇒ Name'],
      path: [row['Tasks ⇒ Asset'], row['Tasks ⇒ Attached to']],
    };
  }
  else if (module === 'forms') {
    newRow.Name_breadcrumb = {
      value: row['All forms ⇒ Name'] || row['Forms ⇒ Name'],
      path: [row['All forms ⇒ Asset'] || row['Forms ⇒ Asset'], row['All forms ⇒ Attached to'] || row['Forms ⇒ Attached to']],
    };
  }
  else if (module === 'documents') {
    newRow.Name_breadcrumb = {
      value: row['Documents ⇒ Name'],
      path: [row['Documents ⇒ Asset'], ...(row['Documents ⇒ Path'] || '').split(', ')],
    };
  }
  else if (module === 'transmittals') {
    if (newRow['Transmittals ⇒ Cancelled']) {
      newRow.Status = 'Cancelled';
    }
    else if (newRow['Transmittals ⇒ Completed']) {
      newRow.Status = 'Completed';
    }
    else if (newRow['Transmittals ⇒ Published']) {
      newRow.Status = 'In progress';
    }
    else {
      newRow.Status = 'In draft';
    }
  }

  return newRow;
}

function filterTableColumns(column) {
  if (!state.resource_id_column)
    return true;
  if (state.resource_id_column.resource_type === 'tasks.task.id')
    return has(tasks_mapping, column.alias);
  if (state.resource_id_column.resource_type === 'forms.form.id')
    return has(forms_mapping, column.alias);
  if (state.resource_id_column.resource_type === 'pm.activity.id')
    return has(activities_mapping, column.alias);
  if (state.resource_id_column.resource_type === 'dms.document.id')
    return has(documents_mapping, column.alias);
  if (state.resource_id_column.resource_type === 'dms.transmittal.id')
    return has(transmittals_mapping, column.alias);
  if (state.resource_id_column.resource_type === 'therm.defect.id')
    return has(defects_mapping, column.alias);
  return false;
}

function applyTableFilters() {
  if (props.type === 'table' && props.filters?.length > 0) {
    const alias_stage_index_map = {};
    const alias_to_column_mapping = {};
    widget.config.query.stages.forEach((stage, index) => {
      stage.columns.forEach((column) => {
        if (isNil(alias_stage_index_map[column.alias]))
          alias_stage_index_map[column.alias] = index;
      });
      stage.columns = stage.columns.filter((column) => {
        alias_to_column_mapping[column.alias] = column;
        return !column.is_aggregation;
      });
      stage.filters = stage.filters.filter((filter) => {
        if (filter.field && alias_to_column_mapping[filter.field]) {
          return false;
        }
        return true;
      });
      stage.orderBy = stage.orderBy.filter((orderBy) => {
        if (orderBy.column && alias_to_column_mapping[orderBy.column]) {
          return false;
        }
        return true;
      });
    });
    if (widget.config.query.stages[0].table) {
      const table = widget.config.query.stages[0].table;
      const table_columns = table?.generateSeries ? [] : bi_store.all_schema_by_name[widget.config.query.stages[0].table].columns.map(col => ({ alias: col.alias, field: col.field, field_type: col.type, table_name: col.table_name, label: col.label, resource_type: col.resource_type }));
      state.resource_id_column = table_columns.find((column) => {
        return ['tasks.task.id', 'forms.form.id', 'pm.activity.id', 'dms.document.id', 'dms.transmittal.id', 'therm.defect.id'].includes(column.resource_type);
      });
      widget.config.query.stages[0].columns = [
        ...widget.config.query.stages[0].columns,
        ...table_columns.filter((column) => {
          return filterTableColumns(column);
        }),
      ];
    }
    widget.config.query.stages.forEach((stage, index) => {
      stage.joins?.forEach((join) => {
        const table = bi_store.all_schema_by_name[join.table.name];
        const columns = table.columns.map(col => ({ field: col.field, alias: col.alias, resource_type: col.resource_type }));
        if (!state.resource_id_column) {
          state.resource_id_column = columns.find((column) => {
            return ['tasks.task.id', 'forms.form.id', 'pm.activity.id', 'dms.document.id', 'dms.transmittal.id', 'therm.defect.id'].includes(column.resource_type);
          });
        }

        widget.config.query.stages[index].columns = uniqBy([
          ...widget.config.query.stages[index].columns,
          ...columns.filter((column) => {
            return filterTableColumns(column);
          }),
        ], 'alias');
      });
    });
    props.filters.forEach((filter) => {
      const stage_index = alias_stage_index_map[filter.field];
      if (stage_index !== undefined) {
        widget.config.query.stages[stage_index].filters.push(filter);
      }
    });
  }
}

async function getTableData() {
  if (props.type === 'table') {
    state.is_loading = true;
    try {
      applyTableFilters();
      const data = await bi_store.getWidgetData(widget.config.query.stages, widget.uid, null, widget.config.quick_filters);
      if (state.resource_id_column) {
        state.resource_id_values = data.data.map(row => row[state.resource_id_column.alias]);
      }
      state.widget_data = data.data;
      state.widget_metadata = data.metadata;

      if (state.resource_id_column?.resource_type === 'tasks.task.id') {
        state.is_tasks_view = true;
      }
      else if (state.resource_id_column?.resource_type === 'forms.form.id') {
        state.is_forms_view = true;
      }
      else if (state.resource_id_column?.resource_type === 'pm.activity.id') {
        state.is_activities_view = true;
      }
      else if (state.resource_id_column?.resource_type === 'dms.document.id') {
        state.is_documents_view = true;
      }
      else if (state.resource_id_column?.resource_type === 'dms.transmittal.id') {
        state.is_transmittals_view = true;
      }
      else if (state.resource_id_column?.resource_type === 'therm.defect.id') {
        state.is_defects_view = true;
      }
    }
    finally {
      state.is_loading = false;
    }
  }
}

function getAdditionalTableSettings(module = null) {
  return {
    enableVirtualization: true,
    offsetHeight: 80,
    bi_table_settings: {
      ...(module
        ? {
            disableVisualSelection: true,
            selectionMode: 'none',
          }
        : {}
      ),
      rowHeaders: true,
      afterOnCellMouseDown(event, coords) {
        event.stopPropagation();
        if (coords.row <= -1)
          return;
        const physical_row = this.toPhysicalRow(coords.row);
        const rowData = this.getSourceDataAtRow?.(physical_row);
        if (module === 'tasks')
          handleViewTask(rowData['Tasks ⇒ ID']);
        else if (module === 'forms')
          handleViewForm(rowData['All forms ⇒ ID'] || rowData['Forms ⇒ ID']);
        else if (module === 'activities')
          handleViewActivity(rowData['Activities ⇒ UID']);
        else if (module === 'documents')
          handleViewDocument(rowData['Documents ⇒ ID']);
        else if (module === 'transmittals')
          handleViewTransmittal(rowData['Transmittals ⇒ ID']);
        else if (module === 'defects')
          handleViewDefect(rowData['Defects ⇒ ID']);
      },

      cells(row, col) {
        const cellProps = {};
        const column = this.instance.getSettings().columns[col];

        if (column.data === 'Name' && ['tasks', 'forms', 'documents'].includes(module)) {
          cellProps.breadcrumb_data_key = `${column.data}_breadcrumb`;
          cellProps.renderer = breadcrumbRenderer;
        }
        else if (column.data === 'Assignees') {
          cellProps.tag_type = 'member';
          cellProps.renderer = tagsRenderer;
        }
        else if (column.data === 'Priority') {
          cellProps.renderer = priorityRenderer;
        }
        else if (column.data === 'Status') {
          cellProps.module = module;
          cellProps.renderer = statusRenderer;
        }
        else if (column.data === 'Tags') {
          cellProps.tag_type = 'tag';
          cellProps.renderer = tagsRenderer;
        }
        else if (column.data === 'Progress' && module === 'activities') {
          cellProps.renderer = progressRenderer;
        }

        return cellProps;
      },
    },
    skipAutoFit: true,
  };
}

function handleViewTask(task_uid) {
  const task = btoa(JSON.stringify({
    id: task_uid,
    store_key: 'task_store',
  }));
  router.replace({
    ...route,
    query: {
      ...route.query,
      task,
    },
  });
}

function handleViewForm(form_uid) {
  const form = btoa(JSON.stringify({
    form_uid,
    store_key: 'form_store',
  }));
  router.replace({
    ...route,
    query: {
      ...route.query,
      form,
    },
  });
}

function handleViewActivity(activity_uid) {
  const schedule_uid = state.resource_id_column.field.replace('activities_', '').replace('→UID', '');
  const route_data = router.resolve({
    name: 'schedule-details',
    params: {
      asset_id: route.params.asset_id,
      schedule_uid,
    },
    query: {
      activity: activity_uid,
    },
  });
  window.open(route_data.href, '_blank');
}

function handleViewDocument(document_uid) {
  const route_data = router.resolve({
    name: 'files-documents-all-files',
    params: {
      asset_id: route.params.asset_id,
    },
    query: {
      document: document_uid,
      from_other_module: true,
    },
  });
  window.open(route_data.href, '_blank');
}

function handleViewTransmittal(transmittal_uid) {
  const route_data = router.resolve({
    name: 'files-transmittals-detail',
    params: {
      asset_id: route.params.asset_id,
      transmittal_uid,
    },
  });
  window.open(route_data.href, '_blank');
}

function handleViewDefect(defect_uid) {
  logger.log('🆘 ~ defect_uid:', defect_uid);
}

onMounted(() => {
  getTableData();
});
</script>

<template>
  <hawk-modal-container
    :options="{
      escToClose: false,
      focusTrap: false, // For handsontable filters input
    }"
    content_class="h-full w-full rounded-none"
  >
    <hawk-modal-header @close="emit('close')">
      <template #title>
        {{ props.widget.name }} ({{ props.filters[0].field }}={{ props.filters[0].value }})
      </template>
    </hawk-modal-header>
    <hawk-modal-content :is_scroll="false" class="!p-0">
      <HawkLoader v-if="state.is_loading" />
      <div v-else>
        <BiTableWidget
          v-if="state.is_tasks_view && props.type === 'table'"
          :id="`${widget.uid}_table`"
          :is-builder="false"
          :config="{}"
          :data="tasks_custom_data.data"
          :widget-builder-metadata="tasks_custom_data.metadata"
          :additional-config="getAdditionalTableSettings('tasks')"
          :is-preview="false"
          class="resource_type_table"
        />
        <BiTableWidget
          v-else-if="state.is_forms_view && props.type === 'table'"
          :id="`${widget.uid}_table`"
          :is-builder="false"
          :config="{}"
          :data="forms_custom_data.data"
          :widget-builder-metadata="forms_custom_data.metadata"
          :additional-config="getAdditionalTableSettings('forms')"
          :is-preview="false"
          class="resource_type_table"
        />
        <BiTableWidget
          v-else-if="state.is_activities_view && props.type === 'table'"
          :id="`${widget.uid}_table`"
          :is-builder="false"
          :config="{}"
          :data="activities_custom_data.data"
          :widget-builder-metadata="activities_custom_data.metadata"
          :additional-config="getAdditionalTableSettings('activities')"
          :is-preview="false"
          class="resource_type_table"
        />
        <BiTableWidget
          v-else-if="state.is_documents_view && props.type === 'table'"
          :id="`${widget.uid}_table`"
          :is-builder="false"
          :config="{}"
          :data="documents_custom_data.data"
          :widget-builder-metadata="documents_custom_data.metadata"
          :additional-config="getAdditionalTableSettings('documents')"
          :is-preview="false"
          class="resource_type_table"
        />
        <BiTableWidget
          v-else-if="state.is_transmittals_view && props.type === 'table'"
          :id="`${widget.uid}_table`"
          :is-builder="false"
          :config="{}"
          :data="transmittals_custom_data.data"
          :widget-builder-metadata="transmittals_custom_data.metadata"
          :additional-config="getAdditionalTableSettings('transmittals')"
          :is-preview="false"
          class="resource_type_table"
        />
        <BiTableWidget
          v-else-if="state.is_defects_view && props.type === 'table'"
          :id="`${widget.uid}_table`"
          :is-builder="false"
          :config="{}"
          :data="defects_custom_data.data"
          :widget-builder-metadata="defects_custom_data.metadata"
          :additional-config="getAdditionalTableSettings('defects')"
          :is-preview="false"
          class="resource_type_table"
        />
        <BiTableWidget
          v-else-if="props.type === 'table'"
          :id="`${widget.uid}_table`"
          :is-builder="false"
          :config="widget.config"
          :data="state.widget_data"
          :widget-builder-metadata="state.widget_metadata"
          :additional-config="getAdditionalTableSettings()"
          :is-preview="false"
        />
        <BiWidget
          v-else
          class="!h-[calc(100vh-130px)]"
          :widget-id="`${widget.uid}_drilldown`"
          :config="widget.config"
          :widget-name="widget.name"
          :is-editing="false"
        />
      </div>
    </hawk-modal-content>
  </hawk-modal-container>
</template>

<style lang="scss" scoped>
.resource_type_table {
  :deep(.handsontable) {
    .htCore td {
      font-size: 13px !important;
      user-select: none !important;
      -webkit-user-select: none !important;
      -moz-user-select: none !important;
      -ms-user-select: none !important;
      border-left: none !important;
      border-right: none !important;
    }

    .htCore th:not(.group) {
      border-left: none !important;
      border-right: none !important;
    }
    .htCore th[role="columnheader"] {
      border-left: none !important;
      border-right: 2px solid !important;
      border-right-color: transparent !important;
      &:hover {
        border-right-color: #e5e7eb !important;
      }
    }
    .htCore th[role="rowheader"] {
      border-left: 1px solid #e5e7eb !important;
      div {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    // Row hover effects
    .htCore tbody tr {
      cursor: pointer !important;
      pointer-events: auto !important;
    }

    .htCore tbody tr:hover td {
      background-color: #f3f4f6 !important;
      transition: background-color 0.15s ease;
    }
  }
}
</style>
